import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

const getFullBreadcrumbTrail = (nodes: IStoriesFolder[], nodeId: string): string => {
  const trail: string[] = [];
  const find = (items: IStoriesFolder[], id: string): boolean => {
    for (const item of items) {
      if (item.id === id) { trail.push(item.displayName ?? ''); return true; }
      if (item.children && find(item.children, id)) { trail.unshift(item.displayName ?? ''); return true; }
    }
    return false;
  };
  find(nodes, nodeId);
  return trail.join(' / ');
};
import { Map } from '@opengov/compose-app/immutable';
import { AsyncJobStatus, IStory } from '@opengov/stories-shared/types';
import moment from 'moment';
import React from 'react';
import { publicationStoryViewPath, publicationViewPath, storyViewPath } from '../../../../routes/paths';
import { ESelectAllStatus, IStoriesFolder, IStoryListMessages, IStoryListSelection } from '../../../../types';
import { getStoryStatusChipParams } from '../../../common/utils';
import { navigateToPublicationStoryView, navigateToStoryView } from '../../../state/navigation';
import { useStoryListContext } from '../storyListContext';
import { styles } from './StoryListTable.styles';
import { getFolderName, getPublicationName, handleRowOnSelectAll, handleStorySelection } from './StoryListTable.utils';
import { StoryListTablePagination } from './StoryListTablePagination';

export type StoryListTableProps = {
  showGlobalNavBar: boolean;
  publicationSelectedStories?: Map<string, IStory>;
  publicationStoryListing?: boolean;
  publicationSelectAllStatus?: ESelectAllStatus;
  stories: IStory[];
  isSelectable: boolean;
  messages: IStoryListMessages;
  isTemplate: boolean;
  entitySubdomain: string;
  isViewingFolder: boolean;
  folderStructure?: IStoriesFolder[];
  canOrganizeStories?: boolean;
  publicationId?: string;
  highlightedPublicationStoriesIds?: string[];
  publicationsList?: { id: string; title: string }[];
  bulkChangeStatusProcessStatus?: string;
  selectAllStories?: boolean;
  totalStoriesCount?: number;
  paginationOptions?: {
    totalRows: number;
    rowsPerPage: number;
    setRowsPerPage: (rowsPerPage: number) => void;
    currentPage: number;
    setPage: (page: number) => void;
    itemName: string;
  };
  publicationSetSelectedStories?(stories: Map<string, IStory>): void;
  publicationSetSelectAllStatus?(selectAllStatus: ESelectAllStatus): void;
  onClick(story: IStory, isTemplate: boolean): void;
  setSelectedList?(newList: IStoryListSelection, entitySubdomain: string): void;
};

export function StoryListTable({
  publicationSelectedStories,
  publicationSetSelectedStories,
  publicationSelectAllStatus,
  publicationSetSelectAllStatus,
  publicationStoryListing,
  stories,
  isSelectable,
  messages,
  isTemplate,
  entitySubdomain,
  folderStructure,
  canOrganizeStories,
  isViewingFolder,
  publicationId,
  highlightedPublicationStoriesIds,
  publicationsList,
  bulkChangeStatusProcessStatus,
  setSelectedList,
  selectAllStories,
  paginationOptions,
  onClick
}: StoryListTableProps) {
  const storyListContext = useStoryListContext();
  const { selectAllStatus, selectedStories, highlightedStoriesIds, setSelectAllStatus, setSelectedStories } =
    storyListContext;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [tableMaxHeight, setTableMaxHeight] = React.useState<string>('calc(100vh - 410px)');

  React.useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const offsetTop = containerRef.current.getBoundingClientRect().top;
        const paginationHeight = paginationOptions ? 52 : 0;
        setTableMaxHeight(`calc(100vh - ${offsetTop + paginationHeight + 8}px)`);
      }
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    if (containerRef.current) observer.observe(document.body);
    window.addEventListener('resize', updateHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, [paginationOptions]);
  let selectAllChecked: boolean | 'mixed';
  if (publicationStoryListing) {
    selectAllChecked =
      publicationSelectAllStatus === ESelectAllStatus.All
        ? true
        : publicationSelectAllStatus === ESelectAllStatus.None
        ? false
        : 'mixed';
  } else {
    selectAllChecked =
      selectAllStatus === ESelectAllStatus.All ? true : selectAllStatus === ESelectAllStatus.None ? false : 'mixed';
  }

  const getClickableLink = (url: string, name: string) => {
    return (
      <Box sx={styles.publicationColumn} data-test="navigate-to-publication-button">
        <a target="_blank" rel="noopener noreferrer" href={url}>
          {name || ''}
        </a>
      </Box>
    );
  };
  return (
    <Box ref={containerRef} sx={styles.tableContainer}>
      <TableContainer sx={{ ...styles.table, maxHeight: tableMaxHeight }} data-test="story-list-table">
        <Table stickyHeader>
          <TableHead data-test="story-list-table-headers">
            <TableRow>
              {isSelectable && (
                <TableCell sx={styles.checkboxHeaderCell} aria-label="Select">
                  <Checkbox
                    id="story-list-table-checkbox-all"
                    checked={selectAllChecked === true}
                    indeterminate={selectAllChecked === 'mixed'}
                    inputProps={{ 'aria-label': 'Select all stories' }}
                    disabled={
                      selectAllStories ||
                      bulkChangeStatusProcessStatus === AsyncJobStatus.InProgress ||
                      bulkChangeStatusProcessStatus === AsyncJobStatus.Enqueued
                    }
                    onChange={
                      selectAllStories ||
                      bulkChangeStatusProcessStatus === AsyncJobStatus.InProgress ||
                      bulkChangeStatusProcessStatus === AsyncJobStatus.Enqueued
                        ? undefined
                        : () => {
                            if (publicationStoryListing) {
                              if (publicationSetSelectedStories && publicationSetSelectAllStatus) {
                                handleRowOnSelectAll(
                                  Map(stories.map((story) => [story._id, story])),
                                  publicationSetSelectedStories,
                                  publicationSelectAllStatus || ESelectAllStatus.None,
                                  publicationSetSelectAllStatus
                                );
                              }
                            } else {
                              handleRowOnSelectAll(
                                Map(stories.map((story) => [story._id, story])),
                                setSelectedStories,
                                selectAllStatus,
                                setSelectAllStatus
                              );
                            }
                          }
                    }
                  />
                </TableCell>
              )}
              {(selectAllStories ||
                bulkChangeStatusProcessStatus === AsyncJobStatus.InProgress ||
                bulkChangeStatusProcessStatus === AsyncJobStatus.Enqueued) && (
                <TableCell sx={styles.emptyCell} aria-hidden="true"></TableCell>
              )}
              <TableCell data-test="name-column" sx={styles.headerCell}>
                {messages.name}
              </TableCell>
              <TableCell data-test="status-column" sx={styles.headerCell}>
                {messages.status}
              </TableCell>
              {canOrganizeStories && !isTemplate && !isViewingFolder && (
                <TableCell data-test="folder-column" sx={styles.headerCell}>
                  {!!publicationId ? messages.location : messages.folder}
                </TableCell>
              )}
              {!isViewingFolder && !publicationStoryListing && !isTemplate && (
                <TableCell data-test="publication-column" sx={styles.headerCell}>
                  {messages.publication}
                </TableCell>
              )}
              <TableCell data-test="created-by-column" sx={styles.headerCell}>
                {messages.createdBy}
              </TableCell>
              <TableCell data-test="created-date-column" sx={styles.headerCell}>
                {messages.createdDate}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody data-test="story-list-table-body">
            {stories.map((story) => {
              const formattedAuthor = `${story.author.name.first} ${story.author.name.last}`;
              const formatedCreatedAt = moment.utc(story.createdAt).fromNow();
              const folderName = getFolderName(isViewingFolder, isTemplate, story, folderStructure);
              const publicationName = getPublicationName(publicationsList, story);
              const folderBreadcrumbs =
                folderStructure && story.folderId ? getFullBreadcrumbTrail(folderStructure, story.folderId) : '';
              const chipParams = getStoryStatusChipParams(story);

              const isSelected = publicationStoryListing
                ? (publicationSelectedStories && publicationSelectedStories.has(story._id)) || selectAllStories
                : selectedStories && selectedStories.has(story._id);
              const isHighlighted = publicationStoryListing
                ? highlightedPublicationStoriesIds?.includes(story._id) || false
                : (highlightedStoriesIds as string[]).includes(story._id);

              const isDisabled =
                (!!bulkChangeStatusProcessStatus &&
                  bulkChangeStatusProcessStatus !== AsyncJobStatus.Success &&
                  bulkChangeStatusProcessStatus !== AsyncJobStatus.Error) ||
                selectAllStories;

              const pubId = publicationId || story.folder?.publicationId;
              const storyViewPathValue = pubId
                ? publicationStoryViewPath(entitySubdomain, story._id, isTemplate, pubId)
                : storyViewPath(entitySubdomain, story._id, isTemplate);

              const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
                if (!isSelectable && onClick) {
                  onClick(story, isTemplate);
                } else if (isSelectable) {
                  const isModifiedClick = event.metaKey || event.ctrlKey;
                  if (isModifiedClick) {
                    window.open(storyViewPathValue, '_blank');
                  } else {
                    pubId
                      ? navigateToPublicationStoryView(entitySubdomain, story._id, isTemplate, pubId)
                      : navigateToStoryView(entitySubdomain, story._id, isTemplate);
                  }
                }
              };

              const isEnabledAndHasLink = isSelectable && !isDisabled;

              return (
                <TableRow
                  data-test={`story-list-row_${story._id}`}
                  data-highlighted={isHighlighted && !isSelected}
                  key={story._id}
                  selected={isSelected}
                  onClick={isDisabled ? undefined : handleRowClick}
                  onAuxClick={isDisabled || !isSelectable ? undefined : (e) => { if (e.button === 1) window.open(storyViewPathValue, '_blank'); }}
                  tabIndex={isDisabled ? undefined : 0}
                  onKeyDown={isDisabled ? undefined : (e) => { if (e.key === 'Enter' && e.target === e.currentTarget) handleRowClick(e as unknown as React.MouseEvent<HTMLTableRowElement>); }}
                  sx={
                    isDisabled
                      ? styles.tableRowDisabled
                      : isHighlighted && !isSelected
                      ? styles.tableRowHighlighted
                      : styles.tableRow
                  }
                >
                  {isSelectable && (
                    <TableCell
                      sx={styles.checkboxBodyCell}
                      onClick={(e) => e.stopPropagation()}
                      data-test="story-list-table-checkbox-cell"
                    >
                      <Checkbox
                        id={`story-list-table-checkbox-${story._id}`}
                        checked={isSelected}
                        disabled={isDisabled}
                        inputProps={{ 'aria-label': `Select story ${story._id}` }}
                        onChange={() => {
                          if (publicationStoryListing) {
                            if (publicationSetSelectedStories && publicationSetSelectAllStatus) {
                              handleStorySelection(
                                story,
                                stories.length,
                                publicationSelectedStories || Map(),
                                publicationSetSelectedStories,
                                publicationSetSelectAllStatus
                              );
                            }
                          } else {
                            handleStorySelection(
                              story,
                              stories.length,
                              selectedStories || Map(),
                              setSelectedStories,
                              setSelectAllStatus
                            );
                          }
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={styles.bodyCell} data-test="story-list-table-title-cell">
                    <Tooltip title={story.title} arrow>
                      <Typography
                        variant="body2"
                        sx={styles.storyTitle}
                        component={isEnabledAndHasLink ? 'a' : 'span'}
                        {...(isEnabledAndHasLink
                          ? {
                              href: storyViewPathValue,
                              onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
                                e.preventDefault();
                              }
                            }
                          : {})}
                      >
                        {story.title}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={styles.bodyCell} data-test="story-list-table-status-cell">
                    {chipParams ? (
                      <Chip
                        label={chipParams.text}
                        variant={chipParams.variant}
                        color={chipParams.color}
                        size="small"
                        sx={styles.chip}
                      />
                    ) : null}
                  </TableCell>
                  {canOrganizeStories && !isTemplate && !isViewingFolder && (
                    <TableCell sx={styles.bodyCell} data-test="story-list-table-folder-cell">
                      <Tooltip
                        title={folderBreadcrumbs || folderName || ''}
                        arrow
                        key={story.folderId}
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          data-test="navigate-to-folder-button"
                          onClick={(e) => {
                            e.preventDefault();
                            if (setSelectedList && folderName && story.folderId) {
                              setSelectedList({ id: story.folderId, displayName: folderName }, entitySubdomain);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              if (setSelectedList && folderName && story.folderId) {
                                setSelectedList({ id: story.folderId, displayName: folderName }, entitySubdomain);
                              }
                            }
                          }}
                        >
                          {folderName || ''}
                        </div>
                      </Tooltip>
                    </TableCell>
                  )}
                  {!isViewingFolder && !publicationStoryListing && !isTemplate && (
                    <TableCell sx={styles.bodyCell} data-test="story-list-table-publication-cell">
                      <Tooltip
                        title={publicationName ?? ''}
                        arrow
                        key={story.folderId}
                      >
                        {getClickableLink(
                          publicationViewPath(entitySubdomain, story.folder?.publicationId),
                          publicationName
                        )}
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell sx={styles.bodyCell}>{formattedAuthor}</TableCell>
                  <TableCell sx={styles.bodyCell}>{formatedCreatedAt}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {paginationOptions && (
        <Box sx={styles.paginationContainer}>
          <StoryListTablePagination
            totalRows={paginationOptions.totalRows}
            page={paginationOptions.currentPage - 1}
            rowsPerPage={paginationOptions.rowsPerPage}
            itemName={paginationOptions.itemName}
            dataTest="publications-stories-pagination"
            onPageChange={(_, newPage) => paginationOptions.setPage(newPage + 1)}
            onRowsPerPageChange={(event) => paginationOptions.setRowsPerPage(parseInt(event.target.value, 10))}
          />
        </Box>
      )}
    </Box>
  );
}
